/**
 * Types for Segment-Level Custom Data Tables, mirroring the `SegmentCustomTableResource` API model.
 * Field names and optionality follow the contract exactly, so responses cast without remapping.
 */

import { DbMainExpression } from 'src/models/common/TPESearchRequest';

export enum CDTSegmentType {
    COST_CENTER = 'COST_CENTER',
    ENTITY = 'ENTITY',
    LOCATION = 'LOCATION',
    PRODUCT_LINE = 'PRODUCT_LINE',
    CHANNEL = 'CHANNEL',
    PROJECT = 'PROJECT',
}

export enum ColumnDataType {
    NUMBER = 'NUMBER',
    PERCENTAGE = 'PERCENTAGE',
    TEXT = 'TEXT',
}

export enum ColumnSourceType {
    MANUAL = 'MANUAL',
    PULL_GL = 'PULL_GL',
    DERIVED_PCT = 'DERIVED_PCT',
    DERIVED_BAL = 'DERIVED_BAL',
}

export enum TableType {
    SEGMENT_LEVEL = 'SEGMENT_LEVEL',
    CLI_LEVEL = 'CLI_LEVEL',
}

export enum ColumnPullStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
}

export enum PeriodType {
    YTD = 'YTD',
    MTD = 'MTD',
    YTDA = 'YTDA',
    MTDA = 'MTDA',
}

export enum ValidationStatus {
    PASS = 'PASS',
    WARNING = 'WARNING',
    FAILED = 'FAILED',
}

export interface SegmentPullCriteria {
    account?: string;
    entity?: string;
    periodType?: PeriodType;
    customCoaId?: string;
}

export interface DerivedConfig {
    type?: string;
    sourceColumnId?: string;
    pctColumnId?: string;
    valueColumnId?: string;
}

export interface ColumnDef {
    columnId?: string;
    displayName?: string;
    dataType?: ColumnDataType;
    sourceType?: ColumnSourceType;
    currency?: string;
    pullCriteria?: SegmentPullCriteria;
    derivedConfig?: DerivedConfig;
}

export interface SegmentTableDefinition {
    tableId?: string;
    tableName?: string;
    tableType?: TableType;
    segmentType?: CDTSegmentType;
    segmentCustomCoaId?: string;
    refreshCadence?: string;
    status?: string;
    columns?: ColumnDef[];
    owner?: string;
    createdDate?: number;
    lastUpdatedDate?: number;
}

/**
 * Identified by `segmentValue`; there is no row id. `segmentName` and `segmentDescription` are
 * row-level FRAMES fields, not entries in `columns` — they have no source type.
 * `values` holds NUMBER/PERCENTAGE cells, `textValues` holds TEXT, both keyed by `columnId`.
 */
export interface SegmentRow {
    segmentValue?: string;
    segmentName?: string;
    segmentDescription?: string;
    values?: Record<string, number>;
    textValues?: Record<string, string>;
    sourceType?: Record<string, string>;
    lastPullTimestamp?: Record<string, number>;
    status?: string;
}

/** Server-computed period totals, keyed by `columnId`. Never summed client-side. */
export interface SegmentAggregates {
    aggregatedTotal?: Record<string, number>;
    unallocatedAmount?: Record<string, number>;
    rowCount?: number;
    lastUpdated?: number;
}

/**
 * `configStatuses` is per-column, so a partial failure is `overallStatus === FAILED` with some
 * entries `COMPLETED` — the contract has no PARTIAL status.
 */
export interface PullStatusResponse {
    pullRequestId?: string;
    overallStatus?: ColumnPullStatus;
    configStatuses?: Record<string, ColumnPullStatus>;
    errors?: Record<string, string>;
    rowsUpserted?: number;
    startedAt?: number;
    completedAt?: number;
}

export interface ValidationEntry {
    type?: string;
    status?: ValidationStatus;
    message?: string;
    details?: string;
}

export interface ValidationReport {
    overallStatus?: ValidationStatus;
    validations?: ValidationEntry[];
    lastValidatedAt?: number;
}

export interface SegmentPeriodInfo {
    period?: string;
    status?: string;
    rowCount?: number;
    lastUpdated?: number;
}

// --- Requests and responses ---

export interface CreateSegmentDataTableRequest {
    tableName: string;
    tableType: TableType;
    segmentType: CDTSegmentType;
    segmentCustomCoaId?: string;
    refreshCadence?: string;
    columns: ColumnDef[];
}

export interface CreateSegmentDataTableResponse {
    tableId?: string;
    tableName?: string;
    tableType?: TableType;
    segmentType?: CDTSegmentType;
    columns?: ColumnDef[];
}

export interface GetSegmentDataTableResponse {
    table?: SegmentTableDefinition;
}

/** Omitting both filters returns every segment table. */
export interface ListSegmentDataTablesRequest {
    segmentType?: CDTSegmentType;
    tableType?: TableType;
}

export interface ListSegmentDataTablesResponse {
    tables?: SegmentTableDefinition[];
}

/**
 * `filterExpression` reuses the existing ATP `DbMainExpression`, so the CDT filter builder applies
 * unchanged. Pagination is cursor-based and forward-only — there is no page count.
 */
export interface SearchSegmentDataTableRowsRequest {
    tableId: string;
    period: string;
    pageSize?: number;
    lastEvaluatedKey?: string;
    filterExpression?: DbMainExpression;
    sortField?: string;
    sortOrder?: string;
}

export interface SearchSegmentDataTableRowsResponse {
    tableId?: string;
    period?: string;
    rows?: SegmentRow[];
    lastEvaluatedKey?: string;
    hasMore?: boolean;
    totalRowsInPeriod?: number;
}

export interface SaveSegmentDataTableResponse {
    tableId?: string;
    period?: string;
    rowsUpdated?: number;
    validationReport?: ValidationReport;
}

export interface UploadSegmentDataTableResponse {
    tableId?: string;
    period?: string;
    rowsUploaded?: number;
    validationReport?: ValidationReport;
}

export interface DownloadSegmentDataTableResponse {
    downloadUrl: string;
}

export interface GetCDTGLPullStatusResponse {
    pullStatus?: PullStatusResponse;
}

export interface GetSegmentAggregatesResponse {
    tableId?: string;
    period?: string;
    aggregates?: SegmentAggregates;
}

export interface GetSegmentValidationsResponse {
    tableId?: string;
    period?: string;
    report?: ValidationReport;
}

export interface GetSegmentPeriodsResponse {
    tableId?: string;
    periods?: SegmentPeriodInfo[];
}

export interface TriggerCDTGLPullResponse {
    pullRequestId: string;
}
